import { useCallback, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useHashTab from '../../components/useHashTab'
import { useToast } from '../../components/toastContext'
import { serverDetail, serverText } from '../../lib/i18n'
import { common as C } from './strings'

/**
 * THE SETTINGS FORM, ONE SECTION AT A TIME.
 *
 * Each settings section opens in a sheet with one Save. Behind that button a section may
 * hold several save actions — the email section is one, the login section four — because
 * the endpoints were cut one per legacy card. The sheet should not have to know that, so
 * this keeps a BASELINE beside the form (what the server last confirmed) and a group is
 * dirty when any of its keys differs from it. Saving a section posts only its dirty
 * groups, in order; a group that fails stays dirty and says why.
 *
 * @param {Record<string, string[]>} sections  each section's save groups, in posting order
 * @param {(group: string, form: object) => Promise<{ok: boolean, message?: string,
 *   nothingToSave?: boolean, unsupported?: boolean}>} save
 * @param {(group: string, form: object) => object|void} [afterSave]  side effects of a
 *   committed group, and any values it puts back (the terms' "updated" box goes off)
 */
export default function useSettingsForm({ sections, save, afterSave }) {
  const toast = useToast()
  const [form, setForm] = useState(null)
  const [baseline, setBaseline] = useState(null)
  const [keys, setKeys] = useState({})
  /* Bumped when a group is saved or reset: it re-keys that group's file pickers, so a
     spent pick clears, and cache-busts its previews — the file at the fixed address
     changed, the address did not. */
  const [rounds, setRounds] = useState({})
  const [saving, setSaving] = useState(false)

  const start = useCallback((initial, groupKeys) => {
    setForm(initial)
    setBaseline(initial)
    setKeys(groupKeys)
  }, [])

  const setValue = useCallback((key, value) => setForm((previous) => ({ ...previous, [key]: value })), [])
  const set = useCallback((key) => (value) => setValue(key, value), [setValue])

  const isDirty = (group) =>
    Boolean(form && baseline) && (keys[group] ?? []).some((key) => !same(form[key], baseline[key]))

  const dirtyIn = (section) => (sections[section] ?? []).filter(isDirty)

  const bump = (groups) =>
    setRounds((current) => {
      const next = { ...current }
      for (const group of groups) next[group] = (next[group] ?? 0) + 1
      return next
    })

  /** Posts the section's dirty groups. Resolves true when every one of them landed. */
  const saveSection = async (section) => {
    const groups = dirtyIn(section)
    if (saving || groups.length === 0) return true
    setSaving(true)

    const snapshot = form
    const confirmed = {}
    const reset = {}
    const committed = []
    const messages = []
    let failed = false

    for (const group of groups) {
      let outcome
      try {
        outcome = await save(group, snapshot)
      } catch {
        outcome = { ok: false, message: '' }
      }

      if (outcome.ok || outcome.nothingToSave) {
        committed.push(group)
        if (outcome.message) messages.push(outcome.message)
        for (const key of keys[group] ?? []) {
          /* A picked file is spent once it is uploaded: the form lets go of it and the
             preview shows the new file from its fixed address. */
          const spent = isFile(snapshot[key])
          confirmed[key] = spent ? null : snapshot[key]
          if (spent) reset[key] = null
        }
        Object.assign(reset, afterSave?.(group, snapshot) ?? null)
      } else {
        failed = true
        toast.error(
          outcome.unsupported ? C.notAvailable : serverDetail(outcome.message) || C.saveFailed,
        )
      }
    }

    setBaseline((previous) => ({ ...previous, ...confirmed, ...reset }))
    setForm((previous) => ({ ...previous, ...reset }))
    if (committed.length) bump(committed)
    if (!failed) toast.success(serverText(messages[0]) || C.saved)
    setSaving(false)
    return !failed
  }

  /** Puts a section back to what the server last confirmed. */
  const resetSection = (section) => {
    if (!baseline) return
    const groups = sections[section] ?? []
    const restored = {}
    for (const group of groups) for (const key of keys[group] ?? []) restored[key] = baseline[key]
    setForm((previous) => ({ ...previous, ...restored }))
    bump(groups.filter(isDirty))
  }

  return {
    form,
    /* What the server last confirmed — what the hub reads, so a draft in an open sheet
       never shows as the section's state. */
    saved: baseline,
    ready: form !== null,
    start,
    set,
    setValue,
    saving,
    isDirty: (section) => dirtyIn(section).length > 0,
    saveSection,
    resetSection,
    round: (group) => rounds[group] ?? 0,
  }
}

/**
 * WHICH SECTION IS ON SCREEN IS THE ADDRESS'S BUSINESS.
 *
 * `/settings#email` opens the email section — the address every inbound link already
 * uses — through the kit's `useHashTab`, so switching tabs rewrites the hash without
 * stacking history. `focus` is a setting a search result asked for: the section finds its
 * field once it is on screen. It arrives in router state when the result was on the other
 * settings page.
 *
 * @param {string} fallback  the section shown when the address names none
 * @param {string[]} ids  the sections this page can show
 */
export function useSection(fallback, ids) {
  const [current, go] = useHashTab(fallback, ids)
  const { state } = useLocation()
  const [focus, setFocus] = useState(() => (state?.focus ? { label: state.focus } : null))

  const select = useCallback(
    (id, label = null) => {
      /* A fresh object each time, so asking for the same field twice still finds it. */
      setFocus(label ? { label } : null)
      go(id)
    },
    [go],
  )

  return { current, select, focus: focus?.label ? focus : null }
}

const isFile = (value) => typeof Blob !== 'undefined' && value instanceof Blob

/** Arrays (the column lists) compare by content; files by identity; the rest as text. */
function same(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    const left = a ?? []
    const right = b ?? []
    return left.length === right.length && left.every((item) => right.includes(item))
  }
  if (isFile(a) || isFile(b)) return a === b
  return String(a ?? '') === String(b ?? '')
}
